import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MenuItem } from '@/types/navigation';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavigationMenuItemProps {
  item: MenuItem;
  currentPath: string;
  onNavigate: (path: string) => void;
  level?: number;
  collapsed?: boolean;
}

export const NavigationMenuItem: React.FC<NavigationMenuItemProps> = ({
  item,
  currentPath,
  onNavigate,
  level = 0,
  collapsed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path === currentPath;
  const isParentActive = item.children?.some((child) => child.path === currentPath);

  const handleClick = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    } else if (item.path && !item.disabled) {
      onNavigate(item.path);
    }
  };

  const Icon = item.icon;

  const menuItemContent = (
    <button
      onClick={handleClick}
      disabled={item.disabled}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive && 'bg-accent text-accent-foreground',
        isParentActive && !isActive && 'text-accent-foreground',
        item.disabled && 'opacity-50 cursor-not-allowed',
        level > 0 && 'ml-4',
        collapsed && 'justify-center px-2'
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            isActive && 'text-primary',
            collapsed && 'h-6 w-6'
          )}
        />
      )}
      
      {!collapsed && (
        <>
          <span className="flex-1 text-left">{item.label}</span>
          
          {item.badge && (
            <Badge variant={item.badgeVariant || 'default'} className="ml-auto">
              {item.badge}
            </Badge>
          )}
          
          {hasChildren && (
            <span className="ml-auto">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          )}
        </>
      )}
    </button>
  );

  return (
    <div>
      {collapsed && item.description ? (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{menuItemContent}</TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col gap-1">
              <p className="font-semibold">{item.label}</p>
              {item.description && (
                <p className="text-xs text-muted-foreground">{item.description}</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        menuItemContent
      )}

      {hasChildren && isOpen && !collapsed && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <NavigationMenuItem
              key={child.id}
              item={child}
              currentPath={currentPath}
              onNavigate={onNavigate}
              level={level + 1}
              collapsed={collapsed}
            />
          ))}
        </div>
      )}

      {item.divider && !collapsed && (
        <div className="my-2 border-t border-border" />
      )}
    </div>
  );
};
